import {orderBy, reject} from "lodash";
import {globalError} from "modules/actions/errors";
import {
	addPlayer,
	fetchFriendPicks,
	fetchFriendPicksByPlayer,
	fetchFriendPicksByPlayerPutSuccess,
	fetchFriendPicksSuccess,
	fetchPicksHistory,
	fetchPredictions,
	getHistoryAPISuccess,
	getPredictionsPutStoreSuccess,
	removePick,
	removePlayers,
} from "modules/actions/streak";
import {getSentryUser} from "modules/selectors";
import {getActiveRound} from "modules/selectors/rounds";
import {getFriendPicks, getHistoryPicks, getPredictionsList} from "modules/selectors/streak";
import {getMatchByIdSelector} from "modules/selectors/units";
import Api from "modules/utils/Api";
import ApiError from "modules/utils/Api/ApiError";
import {captureSentryError} from "modules/utils/sentry";
import {call, put, select} from "typed-redux-saga/macro";

export const fetchPredictionsSaga = function* (action: ReturnType<typeof fetchPredictions>) {
	try {
		const result = yield* call(Api.Prediction.get);

		ApiError.CHECK(result.data);

		yield* put(
			getPredictionsPutStoreSuccess({
				...result.data.success,
				...action.payload,
			})
		);
	} catch (e) {
		const user = yield* select(getSentryUser);
		captureSentryError("fetchPredictionsSaga", action, e, user);
		yield* put(globalError(e));
	}
};

export const fetchLivePredictionsSaga = function* () {
	try {
		const round = yield* select(getActiveRound);

		if (round) {
			yield* put(fetchPredictions({roundId: round.id}));
		}
	} catch (e) {
		yield* put(globalError(e));
	}
};

export const addPlayerSaga = function* (action: ReturnType<typeof addPlayer>) {
	try {
		const matchSelector = yield* select(getMatchByIdSelector);
		const predictions = yield* select(getPredictionsList);
		const match = matchSelector(action.payload.unitId);

		if (match) {
			const mergedPredictions = [
				...predictions.map((it) => ({
					playerId: it.playerId,
					unitId: it.unitId,
				})),
				action.payload,
			];

			const sortPredictions = orderBy(
				mergedPredictions,
				(it) => {
					const predictionMatch = matchSelector(it.unitId);
					if (!predictionMatch) {
						return 0;
					}
					return new Date(predictionMatch.startDateTime).getTime();
				},
				"asc"
			);

			const result = yield* call(Api.Prediction.save, {
				predictions: sortPredictions,
			});

			ApiError.CHECK(result.data);

			yield* put(
				getPredictionsPutStoreSuccess({
					predictions: result.data.success.predictions,
					resentHistory: result.data.success.resentHistory,
					roundId: match.roundId,
				})
			);
		}
	} catch (e) {
		const user = yield* select(getSentryUser);
		captureSentryError("addPlayerSaga", action, e, user);
		yield* put(globalError(e));
	}
};

export const removePlayersSaga = function* (action: ReturnType<typeof removePlayers>) {
	try {
		const {roundId} = action.payload;

		const result = yield* call(Api.Prediction.save, {
			predictions: [],
		});

		ApiError.CHECK(result.data);

		yield* put(
			getPredictionsPutStoreSuccess({
				predictions: result.data.success.predictions,
				resentHistory: result.data.success.resentHistory,
				roundId,
			})
		);
	} catch (e) {
		const user = yield* select(getSentryUser);
		captureSentryError("removePlayersSaga", action, e, user);
		yield* put(globalError(e));
	}
};

export const removePlayerSaga = function* (action: ReturnType<typeof removePick>) {
	try {
		const predictions = yield* select(getPredictionsList);

		const filteredPredictions = reject(predictions, (it) => {
			return it.unitId === action.payload.unitId && it.playerId === action.payload.playerId;
		});

		const result = yield* call(Api.Prediction.save, {
			predictions: filteredPredictions.map(({unitId, playerId}) => ({unitId, playerId})),
		});

		ApiError.CHECK(result.data);

		yield* put(
			getPredictionsPutStoreSuccess({
				...result.data.success,
				...action.payload,
			})
		);
	} catch (e) {
		const user = yield* select(getSentryUser);
		captureSentryError("removePlayerSaga", action, e, user);
		yield* put(globalError(e));
	}
};

export const fetchPicksHistorySaga = function* (action: ReturnType<typeof fetchPicksHistory>) {
	try {
		const history = yield* select(getHistoryPicks);

		const response = yield* call(Api.Prediction.history, {
			page: action.payload?.page || 1,
		});

		ApiError.CHECK(response.data);

		yield* put(
			getHistoryAPISuccess({
				predictions:
					action.payload?.page > 1
						? [...history.predictions, ...response.data.success.predictions]
						: response.data.success.predictions,
				nextPage: response.data.success.nextPage,
			})
		);
	} catch (e) {
		const user = yield* select(getSentryUser);
		captureSentryError("fetchPicksHistorySaga", action, e, user);
		yield* put(globalError(e));
	}
};

export const fetchPicksHistorySagaLive = function* () {
	try {
		const response = yield* call(Api.Prediction.history, {
			page: 1,
		});

		ApiError.CHECK(response.data);

		yield* put(
			getHistoryAPISuccess({
				predictions: response.data.success.predictions,
				nextPage: response.data.success.nextPage,
			})
		);
	} catch (e) {
		const user = yield* select(getSentryUser);
		captureSentryError("fetchPicksHistorySagaLive", "none", e, user);
		yield* put(globalError(e));
	}
};

export const fetchFriendPicksSaga = function* (action: ReturnType<typeof fetchFriendPicks>) {
	try {
		const history = yield* select(getFriendPicks);

		const response = yield* call(Api.Prediction.friends_history, {
			page: action.payload.page,
		});

		ApiError.CHECK(response.data);

		const isFirstPage = action.payload.page === 1;

		const historyArray = isFirstPage
			? response.data.success.history
			: [...history.history, ...response.data.success.history];

		yield* put(
			fetchFriendPicksSuccess({
				history: historyArray,
				nextPage: response.data.success.nextPage,
			})
		);
	} catch (e) {
		const user = yield* select(getSentryUser);
		captureSentryError("fetchFriendPicksSaga", "none", e, user);
		yield* put(globalError(e));
	}
};

export const fetchFriendPicksSagaLiveScore = function* () {
	yield put(fetchFriendPicks({page: 1}));
};

export const fetchFriendPicksByPlayerSaga = function* (
	action: ReturnType<typeof fetchFriendPicksByPlayer>
) {
	try {
		const response = yield* call(Api.Prediction.friends_selected_player, action.payload);

		ApiError.CHECK(response.data);

		yield* put(
			fetchFriendPicksByPlayerPutSuccess({
				...action.payload,
				...response.data.success,
			})
		);
	} catch (e) {
		const user = yield* select(getSentryUser);
		captureSentryError("fetchFriendPicksByPlayerSaga", action, e, user);
		yield* put(globalError(e));
	}
};

export default addPlayerSaga;
